import React, { ReactNode } from 'react';
import classNames from 'classnames';
import SelectableBase, {
  ExtendableSelectableBaseProps,
} from '../SelectableBase';
import Text from '../Text';
import Inline from '../Inline';
import Box from '../Box';
import {
  ResponsiveValue,
  getResponsiveValue,
  processStylingProps,
} from '../_internal/styling';
import IconCheckmark16 from '../icons/IconCheckmark16';
import useScreenSize from '../_internal/useScreenSize';
import logger from '../_internal/logger';

import styles from './checkbox.module.scss';

export type Container = (props: {
  checkboxUi: ReactNode;
  disabled: boolean;
  isSelected: boolean;
}) => JSX.Element;

interface Props {
  /**
   * Checkbox label contents
   */
  children?: ReactNode;
  /**
   * A render prop that wraps the checkbox, allowing for additional clickable content around the checkbox UI
   */
  container?: Container;

  /**
   * Size of the checkbox. This value is responsive and may be configured for multiple screen sizes.
   *
   * @default 'default'
   */
  size?: ResponsiveValue<'default' | 'small'>;
}

export type CheckboxProps = Omit<ExtendableSelectableBaseProps<Props>, 'like'>;

// The default container does nothing (returns back the contents)
// NOTE: We may want to create & export Container render props for common layouts
const DEFAULT_CONTAINER: Container = ({ checkboxUi }) => <>{checkboxUi}</>;

const DEFAULT_SIZE = 'default';

/**
 * The `Checkbox` toggles an option on or off.
 */
const Checkbox = ({
  'aria-label': ariaLabel,
  container = DEFAULT_CONTAINER,
  children,
  size = DEFAULT_SIZE,
  ...rootProps
}: CheckboxProps) => {
  const isDefaultContainer = container === DEFAULT_CONTAINER;

  if (!children && !ariaLabel && isDefaultContainer) {
    logger.warn(
      'Checkbox must specify a label (via `children`), a `container` or `aria-label` to satisfy accessibility needs',
    );
  }

  const screenSize = useScreenSize();
  const responsiveSize = getResponsiveValue(screenSize, size, DEFAULT_SIZE);

  // Checkbox sets styling props on the hidden input element in SelectableBase,
  // which is unintended behavior. We will likely remove this entirely in the
  // future (as opposed to replacing it with dangerous props), so starting to
  // warn about it now. Intentionally not adding style-related props to the type
  // definition because this was unintended behavior.
  // TLDR: styling props do not do anything because they are added to hidden
  // inputs.
  const selectableBaseProps = processStylingProps(rootProps, 'Checkbox', {
    stylingProps: 'warn',
    dangerousStylingProps: 'remove',
  });

  return (
    <SelectableBase
      like="checkbox"
      aria-label={ariaLabel}
      {...selectableBaseProps}
    >
      {({ input, isSelected, disabled, ...boxProps }) => (
        <Box
          className={classNames(styles.root, styles[`size-${responsiveSize}`], {
            [styles['is-selected']]: isSelected,
            [styles['is-disabled']]: disabled,
          })}
          {...boxProps}
        >
          {container({
            isSelected,
            disabled,
            checkboxUi: (
              <Inline spacing={0.5} display="inline-flex">
                <span className={styles.toggle}>
                  <IconCheckmark16 purpose="decorative" color="white" />
                  {input}
                </span>
                {children && (
                  <Text
                    scale={responsiveSize === 'default' ? '3' : '2'}
                    height="standard"
                    family="regular"
                    color={disabled ? 'gray-45' : undefined}
                    className={styles.content}
                  >
                    {children}
                  </Text>
                )}
              </Inline>
            ),
          })}
        </Box>
      )}
    </SelectableBase>
  );
};

export default Checkbox;
